'use client';

import type { DragEndEvent } from '@dnd-kit/core';
import type { ComponentType, ReactNode } from 'react';
import { useState } from 'react';
import { closestCenter, DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';

import { SortableRow } from './sortable-row';

type SortableRowListProps = {
  readonly order: readonly string[];
  readonly onReorder: (next: readonly string[]) => void;
  readonly labelFor: (id: string) => string;
  readonly descriptionFor?: (id: string) => string | undefined;
  readonly iconFor?: (id: string) => ComponentType<{ readonly className?: string }> | undefined;
  /** A per-row control (e.g. a hide/show `Switch`) rendered at the trailing edge of that row. */
  readonly trailingFor?: (id: string) => ReactNode;
  readonly rowClassName?: string;
};

/** Moves `activeId` to `overId`'s slot. Exported for the unit test that dnd-kit's own drag can't reach. */
export function reorderById(order: readonly string[], activeId: string, overId: string): readonly string[] | undefined {
  const from = order.indexOf(activeId);
  const to = order.indexOf(overId);
  if (from === -1 || to === -1 || from === to) return undefined;
  return arrayMove([...order], from, to);
}

export function SortableRowList({
  order,
  onReorder,
  labelFor,
  descriptionFor,
  iconFor,
  trailingFor,
  rowClassName,
}: SortableRowListProps) {
  // Pointer only. dnd-kit's `KeyboardSensor` never advanced past the dragged row here (every arrow
  // press announced "moved over" the row itself, in both panes, under either collision strategy and
  // with `MeasuringStrategy.Always`), so the keyboard path is the per-row move buttons instead.
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const [status, setStatus] = useState('');

  function moveItem(from: number, to: number) {
    if (to < 0 || to >= order.length) return;
    const id = order[from];
    if (id === undefined) return;
    onReorder(arrayMove([...order], from, to));
    setStatus(`${labelFor(id)} moved to position ${to + 1} of ${order.length}.`);
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (over === null || typeof active.id !== 'string' || typeof over.id !== 'string') return;
    const next = reorderById(order, active.id, over.id);
    if (next !== undefined) onReorder(next);
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      // Defaults interpolate the raw id, which is a section key ("relationship") in the Overview pane.
      accessibility={{
        announcements: {
          onDragStart: ({ active }) => `Picked up ${labelFor(String(active.id))}.`,
          onDragOver: ({ active, over }) =>
            over === null ? undefined : `${labelFor(String(active.id))} moved over ${labelFor(String(over.id))}.`,
          onDragEnd: ({ active, over }) =>
            over === null
              ? `${labelFor(String(active.id))} was dropped.`
              : `${labelFor(String(active.id))} was dropped over ${labelFor(String(over.id))}.`,
          onDragCancel: ({ active }) => `Reordering ${labelFor(String(active.id))} was cancelled.`,
        },
      }}
    >
      <SortableContext items={[...order]} strategy={verticalListSortingStrategy}>
        <ul className="flex flex-col divide-y divide-[var(--border-light)]">
          {order.map((id, index) => (
            <li key={id}>
              <SortableRow
                id={id}
                label={labelFor(id)}
                description={descriptionFor?.(id)}
                icon={iconFor?.(id)}
                trailing={trailingFor?.(id)}
                className={rowClassName}
                index={index}
                count={order.length}
                onMove={moveItem}
              />
            </li>
          ))}
        </ul>
      </SortableContext>
      <div role="status" aria-live="polite" className="sr-only">
        {status}
      </div>
    </DndContext>
  );
}
